import React, { useState, useEffect, useCallback } from 'react';
import { buildApiUrl, authHeaders } from '../../config/api';
import './OrderExtract.css';

export interface ExtractedPerson {
  first_name: string;
  middle_name: string;
  last_name: string;
  family_name: string;
  birth_date: string;
  death_date: string;
  wedding_date: string;
  verse: string;
  additional_texts: string[];
  notes: string[];
}

export interface ExtractionMeta {
  correctedFields: string[];
  requestId: string;
  sourceImage: { data_base64: string; media_type: string };
  sharedText: string[];
}

interface OrderExtractProps {
  apiUrl?: string;
  onApply: (persons: ExtractedPerson[], meta: ExtractionMeta) => void;
  onClose?: () => void;
  applyLabel?: string;
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name (incl. suffix)',
  family_name: 'Family Name',
  birth_date: 'Date of Birth',
  death_date: 'Date of Death',
  wedding_date: 'Wedding Date',
  verse: 'Verse',
};

const DATE_FIELDS = ['birth_date', 'death_date', 'wedding_date'];
const CONFIRM_DATES = ['birth_date', 'death_date'];
const TEXTAREAS = ['verse'];
const MAX_ADDITIONAL = 8;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function fullDate(normalized: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized || '');
  if (!m) return null;
  const mo = +m[2];
  if (mo < 1 || mo > 12) return null;
  return `${MONTHS[mo - 1]} ${+m[3]}, ${+m[1]}`;
}

const ACCEPTED = (f: File | null) => !!f && (f.type.startsWith('image/') || f.type === 'application/pdf');

function fileToPayload(file: File, cb: (payload: any) => void) {
  const reader = new FileReader();
  reader.onload = () => {
    const [, b64] = String(reader.result).split(',', 2);
    cb({
      image_base64: b64,
      media_type: file.type || 'image/png',
      dataUrl: reader.result,
      blobUrl: URL.createObjectURL(file)
    });
  };
  reader.readAsDataURL(file);
}

function memorialToValues(m: any): ExtractedPerson {
  const v: any = {};
  for (const f of Object.keys(FIELD_LABELS)) {
    v[f] = (m[f] && m[f].value) || '';
  }
  for (const f of DATE_FIELDS) {
    const full = m[f] && fullDate(m[f].normalized);
    if (full) v[f] = full;
  }
  v.additional_texts = (m.additional_texts || []).map((a: any) => a.value || '').filter(Boolean);
  if (v.additional_texts.length === 0) v.additional_texts = [''];
  v.notes = (m.notes || []).map((n: any) => n.value || '').filter(Boolean);
  return v as ExtractedPerson;
}

export const OrderExtract: React.FC<OrderExtractProps> = ({
  apiUrl = '/api/order-extract',
  onApply,
  onClose,
  applyLabel = 'Add to Personal Details'
}) => {
  const [stage, setStage] = useState<'intake' | 'loading' | 'review' | 'error'>('intake');
  const [error, setError] = useState<string | null>(null);
  const [image, setImage] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [persons, setPersons] = useState<ExtractedPerson[]>([]);
  const [corrected, setCorrected] = useState<Set<string>>(new Set());
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [activeSources, setActiveSources] = useState<string[]>([]);
  const [zoom, setZoom] = useState<number>(0);

  const endpointUrl = buildApiUrl(apiUrl);

  const runExtraction = useCallback((payload: any) => {
    setImage(payload);
    setStage('loading');
    setError(null);
    fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ image_base64: payload.image_base64, media_type: payload.media_type }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const errBody = await r.json().catch(() => ({}));
          throw new Error(errBody.detail || errBody.message || `Server error ${r.status}`);
        }
        return r.json();
      })
      .then((data) => {
        setResult(data);
        const memorials = data?.extraction?.memorials || [];
        setPersons(memorials.map(memorialToValues));
        setCorrected(new Set());
        setConfirmed({});
        setStage('review');
      })
      .catch((e: any) => {
        setError(e.message || 'Failed to process document');
        setStage('error');
      });
  }, [endpointUrl]);

  useEffect(() => {
    if (stage !== 'intake') return;
    const onPaste = (e: Event) => {
      const clipEvt = e as ClipboardEvent;
      const items = Array.from(clipEvt.clipboardData?.items || []);
      const item = items.find((i) => i.type.startsWith('image/'));
      if (item) {
        const file = item.getAsFile();
        if (file) fileToPayload(file, runExtraction);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [stage, runExtraction]);


  const errors = (result?.warnings || []).filter((w: any) => w.level === 'error');
  const warns = (result?.warnings || []).filter((w: any) => w.level === 'warn' && w.field == null);
  const fieldWarns = (pi: number, f: string) =>
    (result?.warnings || []).filter(
      (w: any) => w.level === 'warn' && w.field === f && (w.memorial_index == null || w.memorial_index === pi)
    );

  const neededConfirms = persons.flatMap((p: any, i) =>
    CONFIRM_DATES.filter((f) => p[f]).map((f) => `p${i}_${f}`)
  );
  const datesOk = neededConfirms.every((k) => confirmed[k]);
  const additionalOk = persons.every(
    (p) => p.additional_texts.filter((t) => t.trim() !== '').length <= MAX_ADDITIONAL
  );
  const canApply = errors.length === 0 && datesOk && additionalOk && persons.length > 0;

  function setField(pi: number, f: string, val: string) {
    setPersons((ps) => ps.map((p, i) => (i === pi ? { ...p, [f]: val } : p)));
    setCorrected((c) => new Set(c).add(`p${pi}_${f}`));
    if (CONFIRM_DATES.includes(f)) setConfirmed((d) => ({ ...d, [`p${pi}_${f}`]: false }));
  }

  function setAdditional(pi: number, ai: number, val: string) {
    setPersons((ps) =>
      ps.map((p, i) => {
        if (i !== pi) return p;
        const arr = [...p.additional_texts];
        arr[ai] = val;
        return { ...p, additional_texts: arr };
      })
    );
    setCorrected((c) => new Set(c).add(`p${pi}_additional_texts`));
  }

  function addAdditional(pi: number) {
    setPersons((ps) =>
      ps.map((p, i) =>
        i === pi && p.additional_texts.length < MAX_ADDITIONAL
          ? { ...p, additional_texts: [...p.additional_texts, ''] }
          : p
      )
    );
  }

  function setNote(pi: number, ni: number, val: string) {
    setPersons((ps) =>
      ps.map((p, i) => {
        if (i !== pi) return p;
        const arr = [...p.notes];
        arr[ni] = val;
        return { ...p, notes: arr };
      })
    );
    setCorrected((c) => new Set(c).add(`p${pi}_notes`));
  }

  function addNote(pi: number) {
    setPersons((ps) =>
      ps.map((p, i) => (i === pi ? { ...p, notes: [...p.notes, ''] } : p))
    );
  }

  function swapPersons() {
    setPersons((ps) => [ps[1], ps[0]]);
    setConfirmed({});
    setCorrected((c) => new Set(c).add('swap'));
  }

  function apply() {
    if (!canApply) return;
    const cleaned = persons.map((p) => ({
      ...p,
      additional_texts: p.additional_texts.filter((t) => t.trim() !== ''),
      notes: (p.notes || []).filter((t) => t.trim() !== ''),
    }));
    onApply(cleaned, {
      correctedFields: Array.from(corrected),
      requestId: result?.request_id || 'req_local',
      sourceImage: { data_base64: image.image_base64, media_type: image.media_type },
      sharedText: (result?.extraction?.unclassified_customer_text || []).map((u: any) => u.text),
    });
  }

  const memAt = (pi: number) => result?.extraction?.memorials?.[pi] || {};
  const fieldSources = (pi: number, f: string) => (memAt(pi)[f] ? memAt(pi)[f].source_ids : []);
  const suggestionsFor = (pi: number, f: string) =>
    (result?.suggestions || []).filter((s: any) => s.memorial_index === pi && s.field === f);

  return (
    <div className="oe-backdrop" onClick={onClose}>
      <div
        className={`oe-modal ${stage === 'review' ? 'oe-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="oe-head">
          <span>
            {stage === 'review'
              ? 'Check extracted fields against source — verify before adding to design'
              : 'Extract Order Data from Image / Screenshot'}
          </span>
          {onClose && (
            <button className="oe-x" onClick={onClose} title="Close">
              ✕
            </button>
          )}
        </div>

        {stage !== 'review' ? (
          <div style={{ padding: '10px 0' }}>
            {stage === 'loading' ? (
              <div className="oe-drop oe-busy">Reading order document... (Extracting fields)</div>
            ) : (
              <div
                className="oe-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (ACCEPTED(f)) fileToPayload(f, runExtraction);
                }}
              >
                <p style={{ fontSize: '15px', color: '#1e293b' }}>
                  <b>Paste</b> screenshot here (<kbd>Win+Shift+S</kbd>, then <kbd>Ctrl+V</kbd>)
                </p>
                <p style={{ margin: '8px 0', fontSize: '13px' }}>
                  or drag & drop a file (PNG, JPG, WebP, PDF, BMP, TIFF)
                </p>
                <label className="oe-browse">
                  Browse File...
                  <input
                    type="file"
                    accept="image/*,.pdf,application/pdf,.bmp,.tif,.tiff"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && ACCEPTED(file)) fileToPayload(file, runExtraction);
                    }}
                  />
                </label>
                {stage === 'error' && <p className="oe-error">⚠ {error}</p>}
              </div>
            )}
          </div>
        ) : (
          <div className="oe-scrollbody">
            {persons.length === 2 && (
              <div className="oe-picker">
                <span>Companion Order detected — both persons will populate together.</span>
                <button onClick={swapPersons}>⇄ Swap Person 1 & Person 2</button>
              </div>
            )}

            {errors.length > 0 && (
              <div className="oe-block">
                <b>Apply blocked due to issues:</b>
                <ul>
                  {errors.map((w: any, i: number) => (
                    <li key={i}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {warns.length > 0 && (
              <div className="oe-warn">
                <ul>
                  {warns.map((w: any, i: number) => (
                    <li key={i}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {(() => {
              const usedIds = new Set<string>();
              const memorials = result?.extraction?.memorials || [];
              for (const m of memorials) {
                for (const key of Object.keys(m)) {
                  const items = Array.isArray(m[key]) ? m[key] : [m[key]];
                  for (const item of items) {
                    (item?.source_ids || []).forEach((id: string) => usedIds.add(id));
                  }
                }
              }
              const rawText = result?.extraction?.raw_text || [];
              const interfaceIds = result?.extraction?.interface_text_ids || [];
              const visible = rawText.filter((t: any) => !interfaceIds.includes(t.id));
              const inBoxes = visible.filter((t: any) => usedIds.has(t.id));
              const notInBoxes = visible.filter((t: any) => !usedIds.has(t.id));

              const line = (t: any) => (
                <div key={t.id} className={`oe-line ${activeSources.includes(t.id) ? 'hl' : ''}`}>
                  {t.text}
                </div>
              );

              return (
                <div className="oe-toprow">
                  <div className="oe-srcbox">
                    <h4>
                      Order Document <span className="oe-zoomhint">(click image to zoom)</span>
                    </h4>
                    {image.media_type === 'application/pdf' ? (
                      <iframe className="oe-pdf" src={image.blobUrl} title="Order PDF" />
                    ) : (
                      <img
                        className="oe-img oe-clickable"
                        src={image.dataUrl}
                        alt="Order screenshot"
                        onClick={() => setZoom(1.5)}
                        title="Click to zoom"
                      />
                    )}
                  </div>
                  <div className="oe-textbox">
                    <h4>Extracted Transcript</h4>
                    <div className="oe-textflow">
                      {inBoxes.map(line)}
                      {notInBoxes.length > 0 && (
                        <div className="oe-textdivider">Also on document — unassigned text:</div>
                      )}
                      {notInBoxes.map(line)}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="oe-cols">
              {persons.map((p: any, pi: number) => (
                <div className="oe-col" key={pi}>
                  <h4>{persons.length > 1 ? `Person ${pi + 1}` : 'Extracted Fields'}</h4>
                  {Object.keys(FIELD_LABELS).map((f) => (
                    <div
                      key={f}
                      className={`oe-field ${CONFIRM_DATES.includes(f) && p[f] ? 'oe-date' : ''}`}
                      onMouseEnter={() => setActiveSources(fieldSources(pi, f))}
                      onMouseLeave={() => setActiveSources([])}
                    >
                      <label>{FIELD_LABELS[f]}</label>
                      {TEXTAREAS.includes(f) ? (
                        <textarea
                          rows={2}
                          value={p[f]}
                          onChange={(e) => setField(pi, f, e.target.value)}
                        />
                      ) : (
                        <input
                          value={p[f]}
                          onChange={(e) => setField(pi, f, e.target.value)}
                        />
                      )}

                      {CONFIRM_DATES.includes(f) && p[f] && (
                        <label className="oe-confirm">
                          <input
                            type="checkbox"
                            checked={!!confirmed[`p${pi}_${f}`]}
                            onChange={(e) =>
                              setConfirmed((d) => ({ ...d, [`p${pi}_${f}`]: e.target.checked }))
                            }
                          />
                          Verified character-by-character with source image
                        </label>
                      )}

                      {fieldWarns(pi, f).map((w: any, i: number) => (
                        <div key={`w_${i}`} className="oe-flag">
                          ⚠ {w.message}
                        </div>
                      ))}

                      {suggestionsFor(pi, f).map((s: any, i: number) =>
                        s.kind === 'suggest' && s.proposed !== p[f] ? (
                          <div key={i} className="oe-suggest">
                            {s.reason}{' '}
                            <button onClick={() => setField(pi, f, s.proposed)}>
                              Use “{s.proposed}”
                            </button>
                          </div>
                        ) : s.kind === 'flag' ? (
                          <div key={i} className="oe-flag">
                            ⚠ {s.reason}
                          </div>
                        ) : null
                      )}
                    </div>
                  ))}

                  <div className="oe-field">
                    <label>
                      Additional Text ({p.additional_texts.length} of {MAX_ADDITIONAL} lines)
                    </label>
                    {p.additional_texts.map((t: string, ai: number) => (
                      <div key={ai} style={{ marginBottom: '6px' }}>
                        <input
                          value={t}
                          onChange={(e) => setAdditional(pi, ai, e.target.value)}
                        />
                      </div>
                    ))}
                    {p.additional_texts.length < MAX_ADDITIONAL && (
                      <button className="oe-addline" onClick={() => addAdditional(pi)}>
                        + Add Line
                      </button>
                    )}
                  </div>

                  <div className="oe-field oe-notes">
                    <label>Notes (not engraved)</label>
                    {(p.notes || []).map((t: string, ni: number) => (
                      <div key={ni} className="oe-noteline">
                        <span className="oe-bullet">•</span>
                        <input
                          value={t}
                          onChange={(e) => setNote(pi, ni, e.target.value)}
                        />
                      </div>
                    ))}
                    <button className="oe-addline" onClick={() => addNote(pi)}>
                      + Add Note
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {zoom > 0 && (
              <div className="oe-zoom" onClick={() => setZoom(0)}>
                <div className="oe-zoombar" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setZoom((z) => Math.min(z * 1.5, 8))}>＋ Zoom in</button>
                  <button onClick={() => setZoom((z) => Math.max(z / 1.5, 1))}>－ Zoom out</button>
                  <button onClick={() => setZoom(1)}>Fit</button>
                  <button onClick={() => setZoom(0)}>✕ Close</button>
                </div>
                <div
                  className="oe-zoomscroll"
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={() => setZoom((z) => Math.min(z * 1.5, 8))}
                >
                  <img
                    src={image.dataUrl}
                    alt="Order document zoomed"
                    style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="oe-actions oe-footer">
          {stage === 'review' ? (
            <>
              <button
                className="oe-apply"
                disabled={!canApply}
                onClick={apply}
                title={canApply ? '' : 'Please verify dates character-by-character first'}
              >
                {applyLabel} {persons.length === 2 ? '(Both Persons)' : ''}
              </button>
              <button
                onClick={() => {
                  setStage('intake');
                  setResult(null);
                }}
              >
                Start Over
              </button>
            </>
          ) : null}
          {onClose && <button onClick={onClose}>Cancel</button>}
        </div>
      </div>
    </div>
  );
};

export default OrderExtract;
